import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiError } from "@/lib/api";
import LegalPageShell from "@/components/LegalPageShell";
import { useScopedPath } from "@/lib/scoped";

const REMARK_PLUGINS = [remarkGfm];

/**
 * Builds the ReactMarkdown `components` map for the legal document body.
 * Kept as a module-level factory (not inline in the render body) so
 * ESLint's `no-unstable-nested-components` rule is satisfied — each call
 * returns a fresh identity but the anonymous function itself lives at
 * module scope.
 */
function makeMarkdownComponents(scope) {
  return {
    a: function ScopedMdLink({ href, children, ...props }) {
      if (typeof href === "string" && href.startsWith("/")) {
        return (
          <Link to={scope(href)} {...props}>
            {children}
          </Link>
        );
      }
      return (
        <a href={href} target="_blank" rel="noreferrer noopener" {...props}>
          {children}
        </a>
      );
    },
  };
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function LegalDocumentPage({ docType, subtitle = "Legal", testId }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const scope = useScopedPath();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/legal/${docType}`);
        if (!cancelled) setDoc(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [docType]);

  // Slug-aware in-app link renderer for the legal markdown. Relative
  // `/cookies`, `/terms`, … links stay inside `/c/[slug]/*` when we're
  // inside a client env. External `https://…` links fall through to a
  // normal `<a target="_blank">`. Memoised on `scope` so ReactMarkdown
  // doesn't remount the anchor subtree on every render.
  const markdownComponents = useMemo(() => makeMarkdownComponents(scope), [scope]);

  return (
    <LegalPageShell
      title={doc?.title ?? (docType === "terms" ? "Terms of Service" : docType === "privacy" ? "Privacy Policy" : "Cookie Policy")}
      subtitle={subtitle}
      lastUpdated={formatDate(doc?.last_updated)}
      testId={testId || `${docType}-page`}
    >
      {error ? (
        <p className="text-[#FF3B30]">{error}</p>
      ) : !doc ? (
        <p className="text-zinc-400 text-sm uppercase tracking-[0.2em]">Loading…</p>
      ) : (
        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>{doc.content}</ReactMarkdown>
      )}
    </LegalPageShell>
  );
}
